/**
 * Converts camelCase to PascalCase
 *
 * @param str A string in camelCase
 *
 * @returns A string in PascalCase
 */
export function camelToPascal(str: string): string {
  return str.slice(0, 1).toUpperCase() + str.slice(1);
}

/**
 * Converts camelCase to snake_case
 *
 * @param str A string in camelCase
 *
 * @returns A string in snake_case
 */
export function camelToSnake(str: string): string {
  return str.replace(/([a-z0-9])([A-Z])/g, "$1_$2").toLowerCase();
}

/**
 * Capitalized the first character in the string
 *
 * @param str A string
 *
 * @return The given string with the first character capitalized
 */
export function capitalize(str: string): string {
  return str.slice(0, 1).toUpperCase() + str.slice(1);
}

/**
 * Converts PascalCase to camelCase
 *
 * @param str A string in PascalCase
 *
 * @returns A string in camelCase
 */
export function pascalToCamel(str: string): string {
  return str.slice(0, 1).toLowerCase() + str.slice(1);
}

/**
 * Converts PascalCase to snake_case
 *
 * @param str A string in PascalCase
 *
 * @returns A string in snake_case
 */
export function pascalToSnake(str: string): string {
  return camelToSnake(pascalToCamel(str));
}

/**
 * Converts snake_case to camelCase
 *
 * @param str A string in snake_case
 *
 * @returns A string in camelCase
 */
export function snakeToCamel(str: string): string {
  return str
    .replaceAll(/_([^_])/g, (_, char) => char.toUpperCase())
    .replaceAll("_", "");
}

/**
 * Converts snake_case to PascalCase
 *
 * @param str A string in snake_case
 *
 * @returns A string in PascalCase
 */
export function snakeToPascal(str: string): string {
  return camelToPascal(snakeToCamel(str));
}

/**
 * Takes a string of space separated text
 * and converts to camelCase.
 *
 * @param str
 *
 * @returns A string in camelCase
 */
export function toCamelCase(str: string): string {
  return str
    .trim()
    .toLowerCase()
    .replaceAll(/\s+([^\s])/g, (_, char) => char.toUpperCase());
}

/**
 * Takes a string of space separated text
 * and converts to PascalCase.
 *
 * @param str
 *
 * @returns A string in camelCase
 */
export function toPascalCase(str: string): string {
  return str
    .trim()
    .toLowerCase()
    .replaceAll(/(?:^|\s+)([^\s])/g, (_, char) => char.toUpperCase());
}

/**
 * Takes a string of space separated text
 * and converts to snake_case.
 *
 * @param str
 *
 * @returns A string in snake_case
 */
export function toSnakeCase(str: string): string {
  return str.trim().toLowerCase().replaceAll(/\s+/g, "_");
}
